import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'

export const useExamQuestions = (result: any) => {
  const [allQuestions, setAllQuestions] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const correctAnswers = result.responses.filter((r: any) => r.isCorrect === true).length
  const wrongAnswers = result.responses.filter((r: any) => r.isCorrect === false).length
  const answeredQuestions = correctAnswers + wrongAnswers
  const total_questions = result.totalQuestions || 30
  const emptyAnswers = total_questions - answeredQuestions

  useEffect(() => {
    const fetchMissingQuestions = async () => {
      setIsLoading(true)

      if (emptyAnswers > 0) {
        try {
          // First, get the test type
          const { data: testData, error: testError } = await supabase
            .from('tests')
            .select('type')
            .eq('id', result.testId)
            .single()

          if (testError) {
            setAllQuestions(result.responses)
            setIsLoading(false)
            return
          }

          let missingQuestions = []

          if (testData.type === 'random') {
            const { data: employeeQuestions, error: empError } = await supabase
              .from('employee_test_questions')
              .select(
                `
                question_id,
                question:question_id (
                  text,
                  category,
                  choices:choices (
                    id,
                    label,
                    text,
                    is_correct
                  )
                )
              `
              )
              .eq('employee_test_id', result.assignmentId)

            if (empError) {
              console.error('Error fetching employee test questions:', empError)
            } else {
              const answeredQuestionIds = result.responses.map((r: any) => r.questionId)
              missingQuestions = (employeeQuestions || [])
                .filter((eq) => !answeredQuestionIds.includes(eq.question_id))
                .map((eq) => ({
                  questionId: eq.question_id,
                  category: eq.question?.category || '',
                  questionText: eq.question?.text || 'Soru metni bulunamadı',
                  choiceId: null,
                  selectedText: '',
                  correctText: eq.question?.choices?.find((c) => c.is_correct)?.text || '',
                  isCorrect: null,
                  answeredAt: null,
                  allChoices: (eq.question?.choices || []).map((choice) => ({
                    id: choice.id,
                    text: choice.text,
                    isCorrect: choice.is_correct,
                    isSelected: false,
                  })),
                }))
            }
          } else if (testData.type === 'manual') {
            const { data: testQuestions, error: testError } = await supabase
              .from('test_questions')
              .select(
                `
                question_id,
                question:question_id (
                  text,
                  category,
                  choices:choices (
                    id,
                    label,
                    text,
                    is_correct
                  )
                )
              `
              )
              .eq('test_id', result.testId)

            if (testError) {
              console.error('Error fetching test questions:', testError)
            } else {
              const answeredQuestionIds = result.responses.map((r: any) => r.questionId)
              missingQuestions = (testQuestions || [])
                .filter((tq) => !answeredQuestionIds.includes(tq.question_id))
                .map((tq) => ({
                  questionId: tq.question_id,
                  category: tq.question?.category || '',
                  questionText: tq.question?.text || 'Soru metni bulunamadı',
                  choiceId: null,
                  selectedText: '',
                  correctText: tq.question?.choices?.find((c) => c.is_correct)?.text || '',
                  isCorrect: null,
                  answeredAt: null,
                  allChoices: (tq.question?.choices || []).map((choice) => ({
                    id: choice.id,
                    text: choice.text,
                    isCorrect: choice.is_correct,
                    isSelected: false,
                  })),
                }))
            }
          }

          // Combine answered and missing questions
          const combinedQuestions = [...result.responses, ...missingQuestions]

          setAllQuestions(combinedQuestions)
        } catch (error) {
          console.error('Error fetching missing questions:', error)
          setAllQuestions(result.responses)
        }
      } else {
        setAllQuestions(result.responses)
      }

      setIsLoading(false)
    }

    fetchMissingQuestions()
  }, [result, emptyAnswers])

  return { allQuestions, isLoading }
}
